import React, { ChangeEvent, FC, useRef, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useInput, useNotify } from 'react-admin';
import { ADMIN_PANEL_SERVER_URL } from '../../../App.config';
import { getToken } from '../../../Admin/Admin.utilities';
import './FileUploadButton.css';

const MAX_FILE_SIZE = 8 * 1024 * 1024;

const FileUploadButton: FC = () => {
  const notify = useNotify();
  const { field } = useInput({ source: 'files' });
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = (): void => inputRef.current?.click();

  const appendUrl = (url: string): void => {
    const current = (field.value as string) || '';
    const next = current.trim() ? `${current.trim()},${url}` : url;
    field.onChange(next);
  };

  const handleChange = async (
    event: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = event.target.files?.[0];
    // Allow re-selecting the same file later.
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      notify('File is larger than the 8MB limit.', { type: 'warning' });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await fetch(`${ADMIN_PANEL_SERVER_URL}/uploads`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || !data.url) {
        throw new Error(data?.error || 'Upload failed.');
      }

      appendUrl(data.url);
      notify('File uploaded.', { type: 'success' });
    } catch (error) {
      console.error('Spaces upload failed:', error);
      notify(error instanceof Error ? error.message : 'Upload failed.', {
        type: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="file-upload-button">
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        hidden
        onChange={handleChange}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={
          uploading ? <CircularProgress size={16} /> : <UploadFileIcon />
        }
        onClick={handlePick}
        disabled={uploading}
      >
        {uploading ? 'Uploading…' : 'Upload image / file'}
      </Button>
      <span className="file-upload-button-hint">
        Uploads to the CDN and appends the URL above. Max 8MB, image or video.
      </span>
    </div>
  );
};

export default FileUploadButton;
